import React, { useEffect, useMemo, useState } from "react";
import { message, Spin, Form, Upload } from "antd";
import { useIsMobile, useIsTablet } from "../../Hook";
import type { UploadFile, UploadProps } from "antd";
import {
  useActiveExpenseCategoriesQuery,
  useActiveExpenseSubcategoriesQuery,
  useActivePaymentMethodsQuery,
  useActiveBankAccountsQuery,
  useCreateExpenseMutation,
} from "../../Services";
import { PaymentMethod, getPaymentMethodCode } from "../../Enums";
import { ManageExpenseCategoryDrawer } from "./ManageExpenseCategoryDrawer";
import dayjs from "dayjs";
import { ExpenseForm } from "./ExpensesForm";
import { PaymentOptionValue } from "../PaymentsOption";
import { drawerFooter, drawerTitle } from "../AppUi";
import { ResizableDrawer } from "../ResizableDrawer/ResizableDrawer";

interface ExpenseDrawerProps {
  open: boolean;
  onClose: () => void;
}

export const ExpenseDrawer: React.FC<ExpenseDrawerProps> = ({
  open,
  onClose,
}) => {
  const [form] = Form.useForm();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();

  const [categoryId, setCategoryId] = useState<string>();
  const [paymentMethodId, setPaymentMethodId] = useState<string>();
  const [manageCategoriesOpen, setManageCategoriesOpen] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [paymentDetails, setPaymentDetails] = useState<PaymentOptionValue>({
    amount: 0,
    note: "",
    bank_account_id: "",
    transaction_no: "",
    reference_no: "",
    cheques: [],
  });

  const { data: categories = [], isFetching: loadingCategories } =
    useActiveExpenseCategoriesQuery({ enabled: open });

  const { data: subcategoriesAll = [], isFetching: loadingSubcategories } =
    useActiveExpenseSubcategoriesQuery({ enabled: open });

  const { data: paymentMethods = [], isFetching: loadingPaymentMethods } =
    useActivePaymentMethodsQuery({ enabled: open });

  const { data: bankAccounts = [], isFetching: loadingBankAccounts } =
    useActiveBankAccountsQuery({ enabled: open });

  const expenseMutation = useCreateExpenseMutation();

  const subcategories = useMemo(() => {
    if (!categoryId) return [];
    return subcategoriesAll.filter((s: any) => s.category_id === categoryId);
  }, [subcategoriesAll, categoryId]);

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((m: any) => m.id === paymentMethodId),
    [paymentMethods, paymentMethodId],
  );
  const paymentMethodCode = selectedPaymentMethod?.code;

  const showBankAccount = [
    getPaymentMethodCode(PaymentMethod.BANK_TRANSFER),
    getPaymentMethodCode(PaymentMethod.CARD),
    getPaymentMethodCode(PaymentMethod.CHEQUE),
  ].includes(paymentMethodCode || "");

  useEffect(() => {
    if (open) {
      if (categories.length > 0 && !categoryId) {
        const id = categories[0].id;
        setCategoryId(id);
        form.setFieldValue("category_id", id);
      }
      if (paymentMethods.length > 0 && !paymentMethodId) {
        const id = paymentMethods[0].id;
        setPaymentMethodId(id);
        form.setFieldValue("payment_method_id", id);
      }
    }
  }, [open, categories, paymentMethods]);

  const resetForm = () => {
    form.resetFields();
    setCategoryId(categories[0]?.id || undefined);
    setPaymentMethodId(paymentMethods[0]?.id || undefined);
    setFileList([]);
    setPaymentDetails({
      amount: 0,
      note: "",
      bank_account_id: "",
      transaction_no: "",
      reference_no: "",
      cheques: [],
    });
    form.setFieldsValue({
      category_id: categories[0]?.id,
      payment_method_id: paymentMethods[0]?.id,
      expense_date: dayjs(),
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();

      formData.append("category_id", values.category_id);
      if (values.subcategory_id) {
        formData.append("subcategory_id", values.subcategory_id);
      }

      formData.append("payment_method_id", values.payment_method_id);
      formData.append("expense_date", values.expense_date.format("YYYY-MM-DD"));

      const details = paymentDetails || {};
      formData.append("amount", String(details.amount || 0));

      if (details.bank_account_id) {
        formData.append("bank_account_id", details.bank_account_id);
      }
      if (details.note?.trim()) {
        formData.append("note", details.note.trim());
      }
      if (details.cheque_no?.trim()) {
        formData.append("cheque_no", details.cheque_no.trim());
      }
      if (details.bank_name?.trim()) {
        formData.append("bank_name", details.bank_name.trim());
      }
      if (details.cheque_date?.trim()) {
        formData.append("cheque_date", details.cheque_date.trim());
      }
      if (details.transaction_no?.trim()) {
        formData.append("transaction_no", details.transaction_no.trim());
      }
      if (details.reference_no?.trim()) {
        formData.append("reference_no", details.reference_no.trim());
      }

      fileList.forEach((file) => {
        if (file.originFileObj) {
          formData.append("attachments[]", file.originFileObj);
        }
      });

      expenseMutation.mutate(formData, {
        onSuccess: (res) => {
          if (res.success) {
            message.success(res.message || "Expense recorded.");
            resetForm();
            onClose();
          } else {
            message.error(res.message || "Failed to record expense.");
          }
        },
        onError: (error: any) => {
          message.error(error?.message || "Failed to record expense.");
        },
      });
    } catch {
      message.warning("Please fill required fields.");
    }
  };

  const isFormLoading =
    loadingCategories ||
    loadingSubcategories ||
    loadingPaymentMethods ||
    loadingBankAccounts;

  const amount = paymentDetails?.amount || 0;

  const uploadProps: UploadProps = {
    fileList,
    multiple: true,
    beforeUpload: (file) => {
      const allowed = ["image/jpeg", "image/png", "application/pdf"];
      if (!allowed.includes(file.type)) {
        message.error(
          `${file.name} is not a supported file type (JPG, PNG, PDF).`,
        );
        return Upload.LIST_IGNORE;
      }
      const isUnder5MB = file.size / 1024 / 1024 < 5;
      if (!isUnder5MB) {
        message.error(`${file.name} exceeds the 5 MB limit.`);
        return Upload.LIST_IGNORE;
      }
      return false;
    },
    onChange: ({ fileList: updated }) => setFileList(updated),
    onRemove: (file) =>
      setFileList((prev) => prev.filter((f) => f.uid !== file.uid)),
  };

  return (
    <>
      <ResizableDrawer
        open={open}
        width={isMobile || isTablet ? "100%" : 600}
        placement="right"
        onClose={handleClose}
        title={drawerTitle(
          "Add Expense",
          "Record a business expense or payment",
        )}
        footer={drawerFooter({
          actions: [
            { label: "Cancel", onClick: handleClose },
            {
              label: "Save Expense",
              type: "primary",
              loading: expenseMutation.isPending,
              disabled: isFormLoading,
              onClick: handleSubmit,
            },
          ],
        })}
      >
        {isFormLoading ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Spin size="default" />
            <span className="text-xs text-gray-400 font-medium">
              Loading expense forms...
            </span>
          </div>
        ) : (
          <ExpenseForm
            form={form}
            categories={categories}
            subcategories={subcategories}
            paymentMethods={paymentMethods}
            bankAccounts={bankAccounts}
            categoryId={categoryId}
            paymentMethodCode={paymentMethodCode}
            amount={amount}
            showBankAccount={showBankAccount}
            uploadProps={uploadProps}
            onCategoryChange={setCategoryId}
            onPaymentMethodChange={setPaymentMethodId}
            onManageCategories={() => setManageCategoriesOpen(true)}
            paymentDetails={paymentDetails}
            onPaymentDetailsChange={setPaymentDetails}
          />
        )}
      </ResizableDrawer>

      <ManageExpenseCategoryDrawer
        open={manageCategoriesOpen}
        onClose={() => setManageCategoriesOpen(false)}
      />
    </>
  );
};
